import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Users, Shield, Trash2, Search, Link, UserPlus, Mail, RefreshCw, X, Clock } from "lucide-react";
import { Input } from "../components/ui/input";

const ROLE_CONFIG = {
  admin: { label: "Admin", color: "bg-red-500/10 text-red-600 dark:text-red-400", desc: "Full access to all features" },
  sub_admin: { label: "Sub Admin", color: "bg-purple-500/10 text-purple-600 dark:text-purple-400", desc: "Manage events & approvals, no user mgmt" },
  team_lead: { label: "Team Lead", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400", desc: "Can update events, view pending" },
  event_host: { label: "Event Host", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400", desc: "Can submit & request changes" },
  vendor: { label: "Vendor", color: "bg-teal-500/10 text-teal-600 dark:text-teal-400", desc: "Can edit own vendor menu & profile" },
};

export default function UserManagementPage() {
  const { authAxios, isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [linkVendorDialog, setLinkVendorDialog] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");

  // Invitations — the only way a new account is created.
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "event_host" });
  const [inviting, setInviting] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/admin/users/invites");
      setInvites(res.data);
    } catch {}
  }, [authAxios]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const sendInvite = async (e) => {
    e?.preventDefault();
    setInviting(true);
    try {
      const api = authAxios();
      await api.post("/admin/users/invite", inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", role: "event_host" });
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the invitation");
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (inv) => {
    try {
      const api = authAxios();
      await api.post(`/admin/users/invites/${inv.invite_id}/resend`);
      toast.success(`Invitation re-sent to ${inv.email}`);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel the invitation for ${inv.email}?`)) return;
    try {
      const api = authAxios();
      await api.delete(`/admin/users/invites/${inv.invite_id}`);
      toast.success("Invitation cancelled");
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const api = authAxios();
        const res = await api.get("/vendors");
        setVendors(res.data);
      } catch {}
    };
    loadVendors();
  }, [authAxios]);

  const handleRoleChange = async (targetUserId, newRole) => {
    try {
      const api = authAxios();
      await api.put(`/admin/users/${targetUserId}/role`, { role: newRole });
      toast.success("Role updated successfully");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleLinkVendor = async () => {
    if (!linkVendorDialog || !selectedVendorId) return;
    try {
      const api = authAxios();
      await api.put(`/users/${linkVendorDialog.user_id}/link-vendor`, { vendor_id: selectedVendorId });
      toast.success("User linked to vendor and role set to Vendor");
      setLinkVendorDialog(null);
      setSelectedVendorId("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link vendor");
    }
  };

  const handleDeleteUser = async (targetUserId) => {
    try {
      const api = authAxios();
      await api.delete(`/admin/users/${targetUserId}`);
      toast.success("User deleted");
      setConfirmDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 md:p-8 lg:p-12 w-full">
        <div className="text-center py-16">
          <Shield className="h-16 w-16 mx-auto text-destructive/30 mb-4" />
          <h2 className="font-heading text-2xl font-bold text-foreground">Access Denied</h2>
          <p className="text-muted-foreground mt-2">Only administrators can manage users.</p>
        </div>
      </div>
    );
  }

  const filtered = users.filter((u) =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">Invite people, then set what they can do</p>
        </div>
        <Button className="h-11 gap-2" onClick={() => setInviteOpen(true)} data-testid="invite-user-btn">
          <UserPlus className="h-4 w-4" aria-hidden="true" />
          Invite user
        </Button>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4">
        {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-2">
            <Badge className={cfg.color}>{cfg.label}</Badge>
            <span className="text-xs text-muted-foreground">{cfg.desc}</span>
          </div>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} className="pl-10"
          data-testid="user-search-input"
        />
      </div>

      {invites.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="pending-invites">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-amber-600" aria-hidden="true" />
              Waiting to accept ({invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.invite_id}
                   className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg bg-card border border-border/50 p-3"
                   data-testid={`invite-${inv.invite_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {inv.name}
                    <span className="text-muted-foreground font-normal"> · {inv.email}</span>
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Badge className={ROLE_CONFIG[inv.role]?.color}>{ROLE_CONFIG[inv.role]?.label || inv.role}</Badge>
                    {inv.expired
                      ? <span className="text-destructive flex items-center gap-1"><Clock className="h-3 w-3" />Link expired — resend</span>
                      : <span>Sent {(inv.last_sent_at || inv.created_at || "").slice(0, 10)}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="h-9 gap-1.5"
                          onClick={() => resendInvite(inv)} data-testid={`invite-resend-${inv.invite_id}`}>
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />Resend
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-destructive"
                          onClick={() => cancelInvite(inv)} data-testid={`invite-cancel-${inv.invite_id}`}>
                    <X className="h-3.5 w-3.5" aria-hidden="true" />Cancel
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3" data-testid="users-list">
          {filtered.map((u) => {
            const roleCfg = ROLE_CONFIG[u.role || "event_host"];
            const isSelf = u.user_id === user?.user_id;
            return (
              <Card key={u.user_id} className="border-border/50 shadow-sm" data-testid={`user-card-${u.user_id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold shrink-0">
                      {u.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {u.name} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={roleCfg.color}>{roleCfg.label}</Badge>
                    {!isSelf && (
                      <>
                        <Select value={u.role || "event_host"} onValueChange={(v) => handleRoleChange(u.user_id, v)}>
                          <SelectTrigger className="w-40" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="sub_admin">Sub Admin</SelectItem>
                            <SelectItem value="team_lead">Team Lead</SelectItem>
                            <SelectItem value="event_host">Event Host</SelectItem>
                            <SelectItem value="vendor">Vendor</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.role !== "vendor" && (
                          <Button
                            variant="outline" size="sm"
                            className="h-8 text-xs gap-1"
                            onClick={() => { setLinkVendorDialog(u); setSelectedVendorId(""); }}
                            data-testid={`link-vendor-${u.user_id}`}
                          >
                            <Link className="h-3 w-3" /> Link Vendor
                          </Button>
                        )}
                        {u.role === "vendor" && u.linked_vendor_id && (
                          <Badge className="bg-teal-500/10 text-teal-600 text-xs">
                            Linked
                          </Badge>
                        )}
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setConfirmDialog(u)}
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite someone</DialogTitle>
          </DialogHeader>
          <form onSubmit={sendInvite} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              They get an email with a link, choose a password (or use Google with this
              address), and land in the app with this role already set.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="inv-name">Name</Label>
              <Input id="inv-name" className="h-11" value={inviteForm.name} required
                     onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                     data-testid="invite-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" className="h-11" value={inviteForm.email} required
                     onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                     data-testid="invite-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger className="h-11" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label} — {cfg.desc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="h-11 gap-1.5" disabled={inviting} data-testid="invite-send-btn">
                <Mail className="h-4 w-4" aria-hidden="true" />
                {inviting ? "Sending…" : "Send invitation"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete <strong>{confirmDialog?.name}</strong> ({confirmDialog?.email})? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteUser(confirmDialog?.user_id)} data-testid="confirm-delete-user-btn">
              Delete User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Vendor Dialog */}
      <Dialog open={!!linkVendorDialog} onOpenChange={() => setLinkVendorDialog(null)}>
        <DialogContent data-testid="link-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Link User to Vendor</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            Link <strong>{linkVendorDialog?.name}</strong> to a vendor profile. This will set their role to <Badge className="bg-teal-500/10 text-teal-600 text-xs">Vendor</Badge> automatically.
          </p>
          <div className="space-y-3">
            <Label>Select Vendor</Label>
            <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
              <SelectTrigger data-testid="vendor-link-select">
                <SelectValue placeholder="Choose a vendor..." />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.vendor_id} value={v.vendor_id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setLinkVendorDialog(null)}>Cancel</Button>
            <Button onClick={handleLinkVendor} disabled={!selectedVendorId} data-testid="confirm-link-vendor-btn">
              Link Vendor
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
